import type { UUID } from '@cobliteam/common-types';

export interface WebhooksDataAPI {
  data: WebhooksContentAPI[];
}

export interface WebhooksContentAPI {
  id: UUID;
  name: string;
  url: string;
  event_types: string[];
  secret_key: string;
  custom_headers: WebhooksCustomHeader;
  created_at: string;
  updated_at: string;
  active: boolean;
  failure_started_at: string | null;
  failure_attempts: number | null;
  failure_reason: string | null;
  deactivated_at: string | null;
}

export interface WebhooksInputErrors {
  name: boolean;
  url: boolean;
  eventTypes: boolean;
  publicKey: boolean;
  customHeaders: boolean;
  test: boolean;
}

// Subset of the iris ROUTE_EVENT_TYPES/ACTIVITY_EVENT_TYPES (GF-780): the four concrete types,
// without the "routes"/"activities" group aliases that iris also accepts and returns.
export const routeEventTypes = ['route_started', 'route_finished'];
export const activityEventTypes = ['activity_completed', 'activity_failed'];

export const webhooksEventTypes = [
  'ignition_on',
  'ignition_off',
  'position',
  'position_sleep',
  'geofence_in',
  'geofence_out',
  'speed',
  'battery_external_low',
  'battery_external_disconnected',
  'battery_external_reconnected',
  // Camera event types (SS-2269)
  'hard_break',
  'fast_acceleration',
  'speedy_turn',
  'tailgating',
  'front_collision_warning',
  'distracted_driving',
  'eyes_closed',
  'phone_usage',
  'smoking',
  'yawn',
  'sos',
  // Microconfig event types
  'ignition_lock_status_changed',
  'allow_list_changed',
  ...routeEventTypes,
  ...activityEventTypes,
];

/**
 * Maps each webhook event type to the fleet product types that can generate it.
 * Events absent from this map are available for all product types.
 * Product types: TELEMETRY, REDE_CAN, CAM, CAM_PRO, CAM_MULTI
 */
export const webhookEventProductTypes: Record<string, string[]> = {
  // Accelerometer events — all device types with motion sensor
  hard_break: ['TELEMETRY', 'REDE_CAN', 'CAM', 'CAM_PRO', 'CAM_MULTI'],
  fast_acceleration: ['TELEMETRY', 'REDE_CAN', 'CAM', 'CAM_PRO', 'CAM_MULTI'],
  speedy_turn: ['TELEMETRY', 'REDE_CAN', 'CAM', 'CAM_PRO', 'CAM_MULTI'],
  // ADAS/DMS standard — camera only (CAM and above)
  tailgating: ['CAM', 'CAM_PRO', 'CAM_MULTI'],
  front_collision_warning: ['CAM', 'CAM_PRO', 'CAM_MULTI'],
  distracted_driving: ['CAM', 'CAM_PRO', 'CAM_MULTI'],
  sos: ['CAM', 'CAM_PRO', 'CAM_MULTI'],
  // DMS premium — CAM_PRO and CAM_MULTI only
  eyes_closed: ['CAM_PRO', 'CAM_MULTI'],
  phone_usage: ['CAM_PRO', 'CAM_MULTI'],
  smoking: ['CAM_PRO', 'CAM_MULTI'],
  yawn: ['CAM_PRO', 'CAM_MULTI'],
};

export interface WebhooksCustomHeader {
  [key: string]: string;
}

export interface WebhookEventTypesResponse {
  telemetry: string[];
  camera: string[];
  microconfig: string[];
  routes: string[];
  activities: string[];
}
